import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  Snackbar,
  Alert,
  InputAdornment,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Autocomplete,
  FormHelperText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageIcon from '@mui/icons-material/Image';
import NoImageIcon from '@mui/icons-material/ImageNotSupported';
import { usePermissions } from '../hooks/usePermissions';
import designMaterialsApi, { DesignMaterial, CreateDesignMaterialDTO } from '../api/designMaterials';
import designApi from '../api/design';
import { materialsApi } from '../api/materials';
import { GarmentPart, GARMENT_PARTS } from '../constants/garmentConstants';
import { makeSafeGridColumns } from '../utils/dataGridHelpers';
import api from '../services/api';
import useMediaQuery from '@mui/material/useMediaQuery';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';

// Form dialog for adding/editing material allocations
const DesignMaterialFormDialog: React.FC<{
  open: boolean;
  designMaterialId: number | null;
  onClose: () => void;
  onSave: () => void;
}> = ({ open, designMaterialId, onClose, onSave }) => {
  // In edit mode, we'll use the existing form data structure
  // In add mode, we'll use a new structure with a materials array
  const [isEditMode] = useState(!!designMaterialId);
  
  // Single material form (for edit mode)
  const [formData, setFormData] = useState<Partial<CreateDesignMaterialDTO>>({
    design_no: '',
    design_company_id: 0,
    material_id: 0,
    garment_part: '',
    is_sample: false,
    allocated_qty: 0
  });
  
  // Multiple materials form (for add mode)
  const [designData, setDesignData] = useState({
    design_no: '',
    design_company_id: 0,
  });
  
  // Array of material allocations for add mode
  const [materialAllocations, setMaterialAllocations] = useState<Array<{
    material_id: number;
    garment_part: string;
    is_sample: boolean;
    allocated_qty: number;
    selectedMaterial: any;
  }>>([{
    material_id: 0,
    garment_part: '',
    is_sample: false,
    allocated_qty: 0,
    selectedMaterial: null
  }]);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [designs, setDesigns] = useState<any[]>([]);
  const [materials, setMaterials] = useState<any[]>([]);
  const [selectedDesign, setSelectedDesign] = useState<any>(null);
  const [selectedMaterial, setSelectedMaterial] = useState<any>(null);
  
  // Form validation
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  useEffect(() => {
    const fetchDesignsAndMaterials = async () => {
      try {
        setLoading(true);
        // Fetch all designs via paginated summary API
        const pageSize = 200;
        let page = 1;
        let allDesignsArr: any[] = [];
        while (true) {
          const resp = await api.get('/designs', { params: { summary: 1, page, pageSize, withCount: 1 } });
          const data = Array.isArray(resp.data) ? resp.data : [];
          allDesignsArr = allDesignsArr.concat(data);
          const total = Number(resp.headers['x-total-count'] || 0);
          if (total > 0) {
            if (page * pageSize >= total) break;
          } else {
            if (data.length < pageSize) break;
          }
          page += 1;
        }
        const materialsResponse = await materialsApi.getAvailable();
        
        // Extract data from the responses
        const designsData = allDesignsArr || [];
        
        // Fix: Properly handle the materials response structure
        let materialsData = [];
        if (materialsResponse && materialsResponse.data) {
          // If response has data property
          materialsData = materialsResponse.data;
        } else if (Array.isArray(materialsResponse)) {
          // If response is directly an array
          materialsData = materialsResponse;
        }
        
        console.log('Fetched materials:', materialsData);
        
        setDesigns(designsData);
        setMaterials(materialsData);
        
        // If editing, fetch the design material data
        if (designMaterialId) {
          const designMaterial = await designMaterialsApi.getById(designMaterialId);
          setFormData({
            design_no: designMaterial.design_no,
            design_company_id: designMaterial.design_company_id,
            material_id: designMaterial.material_id,
            garment_part: designMaterial.garment_part,
            is_sample: designMaterial.is_sample,
            allocated_qty: designMaterial.allocated_qty
          });
          
          // Set selected design and material from the arrays
          const design = designsData.find((d: any) => d.design_no === designMaterial.design_no);
          let material = materialsData.find((m: any) => m.id === designMaterial.material_id);
          // If not found, fetch by ID and add to materialsData
          if (!material) {
            try {
              material = await materialsApi.getById(designMaterial.material_id);
              if (material) {
                materialsData = [...materialsData, material];
                setMaterials(materialsData);
              }
            } catch (e) {
              // ignore if not found
            }
          }
          setSelectedDesign(design || null);
          setSelectedMaterial(material || null);
        }
        
        setLoading(false);
      } catch (err: any) {
        console.error('Error fetching data:', err);
        setError(err.message || 'Failed to load data');
        setLoading(false);
      }
    };
    
    fetchDesignsAndMaterials();
  }, [designMaterialId]);
  
  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};
    
    if (isEditMode) {
      // Edit mode validation
      if (!formData.design_no) {
        newErrors.design_no = 'Design is required';
      }
      
      if (!formData.material_id) {
        newErrors.material_id = 'Material is required';
      }
      
      if (!formData.garment_part) {
        newErrors.garment_part = 'Garment part is required';
      }
      
      if (!formData.allocated_qty || formData.allocated_qty <= 0) {
        newErrors.allocated_qty = 'Allocated quantity must be greater than 0';
      }
    } else {
      // Add mode validation (multiple materials)
      if (!designData.design_no) {
        newErrors.design_no = 'Design is required';
      }
      
      // Validate each material allocation
      materialAllocations.forEach((allocation, idx) => {
        if (!allocation.material_id) {
          newErrors[`material_${idx}`] = 'Material is required';
        }
        
        if (!allocation.garment_part) {
          newErrors[`garment_part_${idx}`] = 'Garment part is required';
        }
        
        if (!allocation.allocated_qty || allocation.allocated_qty <= 0) {
          newErrors[`allocated_qty_${idx}`] = 'Allocated quantity must be greater than 0';
        }
      });
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleDesignChange = (event: any, newValue: any) => {
    if (newValue) {
      setSelectedDesign(newValue);
      if (isEditMode) {
        setFormData({
          ...formData,
          design_no: newValue.design_no,
          design_company_id: newValue.company_id
        });
      } else {
        setDesignData({
          design_no: newValue.design_no,
          design_company_id: newValue.company_id
        });
      }
    } else {
      setSelectedDesign(null);
      if (isEditMode) {
        setFormData({
          ...formData,
          design_no: '',
          design_company_id: 0
        });
      } else {
        setDesignData({
          design_no: '',
          design_company_id: 0
        });
      }
    }
  };
  
  const handleMaterialChange = (event: any, newValue: any) => {
    if (isEditMode) {
      // Edit mode - single material
      if (newValue) {
        setSelectedMaterial(newValue);
        setFormData({
          ...formData,
          material_id: newValue.id
        });
      } else {
        setSelectedMaterial(null);
        setFormData({
          ...formData,
          material_id: 0
        });
      }
    }
  };
  
  // Handle material change for multi-material mode
  const handleMultiMaterialChange = (index: number, event: any, newValue: any) => {
    const updatedAllocations = [...materialAllocations];
    if (newValue) {
      updatedAllocations[index] = {
        ...updatedAllocations[index],
        material_id: newValue.id,
        selectedMaterial: newValue
      };
    } else {
      updatedAllocations[index] = {
        ...updatedAllocations[index],
        material_id: 0,
        selectedMaterial: null
      };
    }
    setMaterialAllocations(updatedAllocations);
  };
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  // Handle input change for multi-material allocations
  const handleMultiInputChange = (index: number, name: string, value: any) => {
    const updatedAllocations = [...materialAllocations];
    updatedAllocations[index] = {
      ...updatedAllocations[index],
      [name]: value
    };
    setMaterialAllocations(updatedAllocations);
  };
  
  const handleSelectChange = (event: any) => {
    const name = event.target.name as keyof CreateDesignMaterialDTO;
    const value = event.target.value;
    
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  // Handle select change for multi-material allocations
  const handleMultiSelectChange = (index: number, event: any) => {
    const { name, value } = event.target;
    handleMultiInputChange(index, name, value);
  };
  
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData({
      ...formData,
      [name]: checked
    });
  };
  
  // Handle checkbox change for multi-material allocations
  const handleMultiCheckboxChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    handleMultiInputChange(index, name, checked);
  };
  
  // Add a new material allocation row
  const handleAddMaterialRow = () => {
    setMaterialAllocations([
      ...materialAllocations,
      {
        material_id: 0,
        garment_part: '',
        is_sample: false,
        allocated_qty: 0,
        selectedMaterial: null
      }
    ]);
  };
  
  // Remove a material allocation row
  const handleRemoveMaterialRow = (index: number) => {
    if (materialAllocations.length > 1) {
      const updatedAllocations = [...materialAllocations];
      updatedAllocations.splice(index, 1);
      setMaterialAllocations(updatedAllocations);
    }
  };
  
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    
    try {
      setLoading(true);
      
      if (isEditMode) {
        // Update existing allocation
        await designMaterialsApi.update(designMaterialId!, {
          allocated_qty: formData.allocated_qty,
          is_sample: formData.is_sample
        });
      } else {
        // Create new allocations (multiple possible)
        const promises = materialAllocations.map(allocation => 
          designMaterialsApi.create({
            design_no: designData.design_no,
            design_company_id: designData.design_company_id,
            material_id: allocation.material_id,
            garment_part: allocation.garment_part,
            is_sample: allocation.is_sample,
            allocated_qty: allocation.allocated_qty
          })
        );
        
        await Promise.all(promises);
      }
      
      setLoading(false);
      onSave();
    } catch (err: any) {
      setError(err.message || 'Failed to save design material allocation');
      setLoading(false);
    }
  };
  
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isEditMode ? 'Edit Material Allocation' : 'Add New Material Allocation'}
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Grid container spacing={3} sx={{ mt: 1 }}>
          {/* Design Selection - Common for both modes */}
          <Grid item xs={12}>
            <Autocomplete
              options={designs}
              getOptionLabel={(option) => `${option.design_no} - ${option.name || 'Unnamed'}`}
              value={selectedDesign}
              onChange={handleDesignChange}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    {option.image_url && (
                      <Box
                        component="img"
                        src={option.image_url}
                        alt={option.design_no}
                        sx={{ width: 40, height: 40, mr: 2, objectFit: 'cover', borderRadius: 1 }}
                      />
                    )}
                    <Typography>{option.design_no} - {option.name || 'Unnamed'}</Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Design"
                  error={!!errors.design_no}
                  helperText={errors.design_no}
                  required
                />
              )}
              disabled={isEditMode}
            />
          </Grid>
          
          {isEditMode ? (
            // Edit mode - single material form
            <>
              {/* Material Selection */}
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={materials}
                  getOptionLabel={(option) => {
                    // Make sure option has all required properties
                    if (!option) return '';
                    const materialCode = option.material_code || 'No Code';
                    const materialType = option.material_type || '';
                    const availableQty = option.available_qty || 0;
                    const uom = option.uom || '';
                    
                    return `${materialCode} - ${materialType} (${availableQty} ${uom})`;
                  }}
                  value={selectedMaterial}
                  onChange={handleMaterialChange}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        {option.image_url ? (
                          <Box
                            component="img"
                            src={option.image_url}
                            alt={option.material_code}
                            sx={{ width: 40, height: 40, mr: 2, objectFit: 'cover', borderRadius: 1 }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              mr: 2,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: '#f5f5f5',
                              borderRadius: 1,
                              border: '1px solid #ddd',
                              color: 'text.secondary',
                              fontSize: 10
                            }}
                          >
                            <ImageIcon fontSize="small" />
                          </Box>
                        )}
                        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                          <Typography fontWeight="medium">
                            {option.material_code || 'No Code'} - {option.material_type || ''}
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="caption" color="text.secondary">
                              Available: {option.available_qty || 0} {option.uom || ''}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Rate: ₹{option.rate} per {option.uom || ''}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Material"
                      error={!!errors.material_id}
                      helperText={errors.material_id}
                      required
                    />
                  )}
                />
              </Grid>
              
              {/* Garment Part */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={!!errors.garment_part} required>
                  <InputLabel>Garment Part</InputLabel>
                  <Select
                    name="garment_part"
                    value={formData.garment_part || ''}
                    onChange={handleSelectChange}
                    label="Garment Part"
                  >
                    {GARMENT_PARTS.map((part: GarmentPart) => (
                      <MenuItem key={part} value={part}>
                        {part}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.garment_part && (
                    <FormHelperText>{errors.garment_part}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              
              {/* Allocated Quantity */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Allocated Quantity"
                  name="allocated_qty"
                  type="number"
                  value={formData.allocated_qty || ''}
                  onChange={handleInputChange}
                  InputProps={{
                    endAdornment: selectedMaterial && (
                      <InputAdornment position="end">
                        {selectedMaterial.uom}
                      </InputAdornment>
                    )
                  }}
                  error={!!errors.allocated_qty}
                  helperText={errors.allocated_qty}
                  required
                />
              </Grid>
              
              {/* Is Sample */}
              <Grid item xs={12}>
                <FormControl>
                  <label
                    htmlFor="is_sample"
                    style={{ display: 'flex', alignItems: 'center' }}
                  >
                    <input
                      type="checkbox"
                      id="is_sample"
                      name="is_sample"
                      checked={!!formData.is_sample}
                      onChange={handleCheckboxChange}
                      style={{ marginRight: '8px' }}
                    />
                    Sample Material (Not for production)
                  </label>
                </FormControl>
              </Grid>
              
              {/* If material is selected, show available quantity */}
              {selectedMaterial && (
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Material Information
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2">
                          Available Quantity: <strong>{selectedMaterial.available_qty} {selectedMaterial.uom}</strong>
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2">
                          Rate: <strong>₹{selectedMaterial.rate} per {selectedMaterial.uom}</strong>
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              )}
            </>
          ) : (
            // Add mode - multiple materials form
            <>
              {/* Add button for materials */}
              <Grid item xs={12}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle1">Material Allocations</Typography>
                  <Button 
                    variant="outlined" 
                    startIcon={<AddIcon />} 
                    onClick={handleAddMaterialRow}
                    disabled={!selectedDesign}
                  >
                    Add Material
                  </Button>
                </Box>
              </Grid>
              
              {/* Material allocation rows */}
              {materialAllocations.map((allocation, index) => (
                <Grid item xs={12} key={index}>
                  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Grid container spacing={2}>
                      {/* Row header with remove button */}
                      <Grid item xs={12}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="subtitle2">Material #{index + 1}</Typography>
                          {materialAllocations.length > 1 && (
                            <IconButton 
                              size="small" 
                              color="error" 
                              onClick={() => handleRemoveMaterialRow(index)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </Box>
                      </Grid>
                      
                      {/* Material Selection */}
                      <Grid item xs={12} sm={6}>
                        <Autocomplete
                          options={materials}
                          getOptionLabel={(option) => {
                            // Make sure option has all required properties
                            if (!option) return '';
                            const materialCode = option.material_code || 'No Code';
                            const materialType = option.material_type || '';
                            const availableQty = option.available_qty || 0;
                            const uom = option.uom || '';
                            
                            return `${materialCode} - ${materialType} (${availableQty} ${uom})`;
                          }}
                          value={allocation.selectedMaterial}
                          onChange={(event, newValue) => handleMultiMaterialChange(index, event, newValue)}
                          renderOption={(props, option) => (
                            <li {...props}>
                              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                {option.image_url ? (
                                  <Box
                                    component="img"
                                    src={option.image_url}
                                    alt={option.material_code}
                                    sx={{ width: 40, height: 40, mr: 2, objectFit: 'cover', borderRadius: 1 }}
                                  />
                                ) : (
                                  <Box
                                    sx={{
                                      width: 40,
                                      height: 40,
                                      mr: 2,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      bgcolor: '#f5f5f5',
                                      borderRadius: 1,
                                      border: '1px solid #ddd',
                                      color: 'text.secondary',
                                      fontSize: 10
                                    }}
                                  >
                                    <ImageIcon fontSize="small" />
                                  </Box>
                                )}
                                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                  <Typography fontWeight="medium">
                                    {option.material_code || 'No Code'} - {option.material_type || ''}
                                  </Typography>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="caption" color="text.secondary">
                                      Available: {option.available_qty || 0} {option.uom || ''}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      Rate: ₹{option.rate} per {option.uom || ''}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                            </li>
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Material"
                              error={!!errors[`material_${index}`]}
                              helperText={errors[`material_${index}`]}
                              required
                            />
                          )}
                        />
                      </Grid>
                      
                      {/* Garment Part */}
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth error={!!errors[`garment_part_${index}`]} required>
                          <InputLabel>Garment Part</InputLabel>
                          <Select
                            name="garment_part"
                            value={allocation.garment_part || ''}
                            onChange={(e) => handleMultiSelectChange(index, e)}
                            label="Garment Part"
                          >
                            {GARMENT_PARTS.map((part: GarmentPart) => (
                              <MenuItem key={part} value={part}>
                                {part}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors[`garment_part_${index}`] && (
                            <FormHelperText>{errors[`garment_part_${index}`]}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                      
                      {/* Allocated Quantity */}
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Allocated Quantity"
                          name="allocated_qty"
                          type="number"
                          value={allocation.allocated_qty || ''}
                          onChange={(e) => handleMultiInputChange(index, 'allocated_qty', Number(e.target.value))}
                          InputProps={{
                            endAdornment: allocation.selectedMaterial && (
                              <InputAdornment position="end">
                                {allocation.selectedMaterial.uom}
                              </InputAdornment>
                            )
                          }}
                          error={!!errors[`allocated_qty_${index}`]}
                          helperText={errors[`allocated_qty_${index}`]}
                          required
                        />
                      </Grid>
                      
                      {/* Is Sample */}
                      <Grid item xs={12} sm={6}>
                        <FormControl>
                          <label
                            style={{ display: 'flex', alignItems: 'center' }}
                          >
                            <input
                              type="checkbox"
                              name="is_sample"
                              checked={!!allocation.is_sample}
                              onChange={(e) => handleMultiCheckboxChange(index, e)}
                              style={{ marginRight: '8px' }}
                            />
                            Sample Material (Not for production)
                          </label>
                        </FormControl>
                      </Grid>
                      
                      {/* Material Information */}
                      {allocation.selectedMaterial && (
                        <Grid item xs={12}>
                          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Material Information
                            </Typography>
                            <Grid container spacing={2}>
                              <Grid item xs={6}>
                                <Typography variant="body2">
                                  Available Quantity: <strong>{allocation.selectedMaterial.available_qty} {allocation.selectedMaterial.uom}</strong>
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="body2">
                                  Rate: <strong>₹{allocation.selectedMaterial.rate} per {allocation.selectedMaterial.uom}</strong>
                                </Typography>
                              </Grid>
                            </Grid>
                          </Paper>
                        </Grid>
                      )}
                    </Grid>
                  </Paper>
                </Grid>
              ))}
            </>
          )}
        </Grid>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Main DesignMaterialPage component
export const DesignMaterialContent: React.FC<{
  selectedDesignNo?: string | null;
}> = ({ selectedDesignNo = null }) => {
  const [designMaterials, setDesignMaterials] = useState<DesignMaterial[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedMaterialId, setSelectedMaterialId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDesign, setSelectedDesign] = useState<string | null>(selectedDesignNo);
  const [allDesigns, setAllDesigns] = useState<any[]>([]);
  
  const { canView, canAdd, canEdit, canDelete } = usePermissions();
  
  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down('sm'));
  
  // Filter design materials by search query and selected design
  const filteredMaterials = designMaterials.filter(material => {
    const matchesSearch = 
      !searchQuery || 
      material.design_no.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (material.material?.material_code || '').toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesDesign = !selectedDesign || material.design_no === selectedDesign;
    
    return matchesSearch && matchesDesign;
  });
  
  // Update selected design when selectedDesignNo prop changes
  useEffect(() => {
    if (selectedDesignNo) {
      setSelectedDesign(selectedDesignNo);
    }
  }, [selectedDesignNo]);
  
  // Define columns for the data grid
  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    { 
      field: 'design_no', 
      headerName: 'Design No', 
      width: 150,
      renderCell: (params: any) => {
        if (!params || !params.row) return '';
        return params.row.design_no || '';
      }
    },
    { 
      field: 'material', 
      headerName: 'Material', 
      width: 200,
      renderCell: (params: any) => {
        if (!params || !params.row) return '';
        if (params.row.material) {
          return `${params.row.material.material_code || ''} - ${params.row.material.material_type || ''}`;
        }
        return '';
      }
    },
    { 
      field: 'allocated_qty', 
      headerName: 'Allocated Qty', 
      width: 150,
      type: 'number',
      renderCell: (params: any) => {
        if (!params || !params.row) return 0;
        return params.row.allocated_qty ?? 0;
      }
    },
    { 
      field: 'consumed_qty', 
      headerName: 'Consumed Qty', 
      width: 150,
      type: 'number',
      renderCell: (params: any) => {
        if (!params || !params.row) return 0;
        return params.row.consumed_qty ?? 0;
      }
    },
    { 
      field: 'available_qty', 
      headerName: 'Available Qty', 
      width: 150,
      type: 'number',
      renderCell: (params: any) => {
        if (!params || !params.row) return 0;
        const allocated = params.row.allocated_qty ?? 0;
        const consumed = params.row.consumed_qty ?? 0;
        return allocated - consumed;
      }
    },
    { 
      field: 'is_sample', 
      headerName: 'Is Sample', 
      width: 120,
      renderCell: (params: any) => {
        if (!params || !params.row) return 'No';
        return params.row.is_sample ? 'Yes' : 'No';
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          {canEdit('designs') && (
            <IconButton 
              size="small"
              onClick={() => handleEdit(params.row.id)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          )}
          {canDelete('designs') && (
            <IconButton 
              size="small"
              onClick={() => handleDelete(params.row.id)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      )
    }
  ];
  
  // Fetch design materials on component mount
  useEffect(() => {
    fetchDesignMaterials();
    fetchDesigns();
  }, []);
  
  // Fetch design materials
  const fetchDesignMaterials = async () => {
    try {
      setLoading(true);
      const data = await designMaterialsApi.getAll();
      setDesignMaterials(data);
      setLoading(false);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch design materials');
      setLoading(false);
    }
  };
  
  // Fetch all designs for filtering
  const fetchDesigns = async () => {
    try {
      const response = await designApi.getAll();
      // Extract data from the response
      setAllDesigns(response.data || []);
    } catch (err: any) {
      console.error('Failed to fetch designs:', err);
    }
  };
  
  // Handle opening the create dialog
  const handleOpenDialog = () => {
    setSelectedMaterialId(null);
    setDialogOpen(true);
  };
  
  // Handle editing a design material
  const handleEdit = async (id: number) => {
    setError(null);
    try {
      // Try to fetch the design material by id (to get latest info)
      await designMaterialsApi.getById(id);
      // Try a dry-run update to check for editability (simulate with PATCH/PUT with no changes)
      // Or, call a new endpoint if you have one for checking editability
      // For now, try to fetch and open dialog; if error, show error
      setSelectedMaterialId(id);
      setDialogOpen(true);
    } catch (err: any) {
      // If backend returns error about usage, show it
      setError(err?.response?.data?.error || err.message || 'Cannot edit this allocation.');
    }
  };
  
  // Handle deleting a design material
  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this material allocation?')) {
      try {
        await designMaterialsApi.delete(id);
        setSuccessMessage('Material allocation deleted successfully');
        fetchDesignMaterials();
      } catch (err: any) {
        setError(err.message || 'Failed to delete material allocation');
      }
    }
  };
  
  // Handle dialog close
  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedMaterialId(null);
  };
  
  // Handle successful save
  const handleSaveSuccess = () => {
    setDialogOpen(false);
    setSelectedMaterialId(null);
    setSuccessMessage('Material allocation saved successfully');
    fetchDesignMaterials();
  };
  
  // Handle design filter change
  const handleDesignFilterChange = (event: any) => {
    setSelectedDesign(event.target.value as string);
  };
  
  return (
    <Box sx={{ width: '100%' }}>
      {/* Header section with title, search and add button */}
      <Grid container spacing={2} sx={{ mb: 3, alignItems: 'center' }}>
        <Grid item xs={12} sm={3}>
          <Typography variant="h5">Design Materials</Typography>
        </Grid>
        
        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search..."
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />
        </Grid>
        
        <Grid item xs={12} sm={3}>
          {!selectedDesignNo && (
            <FormControl fullWidth size="small">
              <InputLabel>Filter by Design</InputLabel>
              <Select
                value={selectedDesign || ''}
                onChange={handleDesignFilterChange}
                label="Filter by Design"
              >
                <MenuItem value="">All Designs</MenuItem>
                {Array.isArray(allDesigns) && allDesigns.map((design) => (
                  <MenuItem key={design.id} value={design.design_no}>
                    {design.design_no}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Grid>
        
        <Grid item xs={12} sm={3}>
          {canAdd('designs') && (
            <Button
              variant="contained"
              fullWidth
              startIcon={<AddIcon />}
              onClick={handleOpenDialog}
            >
              Add Material
            </Button>
          )}
        </Grid>
      </Grid>
      
      {/* Error message */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {/* Success message */}
      <Snackbar
        open={Boolean(successMessage)}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
      
      {/* Loading indicator */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        isMobile ? (
          <Grid container spacing={2}>
            {filteredMaterials.map((material) => (
              <Grid item xs={12} key={material.id}>
                <Card sx={{ mb: 2, borderRadius: 2, boxShadow: 1 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, alignItems: 'center', mb: 1 }}>
                      {/* Design Image Thumbnail */}
                      {material.design && material.design.image_url ? (
                        <Box
                          component="img"
                          src={material.design.image_url}
                          alt={material.design_no}
                          sx={{ width: 48, height: 64, objectFit: 'cover', borderRadius: 1, border: '1px solid #eee', mr: 1 }}
                        />
                      ) : (
                        <NoImageIcon sx={{ width: 48, height: 64, color: 'text.disabled', mr: 1 }} />
                      )}
                      {/* Material Image Thumbnail */}
                      {material.material?.image_url ? (
                        <Box
                          component="img"
                          src={material.material.image_url}
                          alt={material.material?.material_code}
                          sx={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 1, border: '1px solid #eee', mr: 1 }}
                        />
                      ) : (
                        <NoImageIcon sx={{ width: 48, height: 48, color: 'text.disabled', mr: 1 }} />
                      )}
                      <Box sx={{ flex: 1 }} />
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Design No: {material.design_no}
                      </Typography>
                      <Typography variant="body2">
                        Material: {material.material?.material_code || ''} - {material.material?.material_type || ''}
                      </Typography>
                      <Typography variant="body2">
                        Allocated Qty: {material.allocated_qty}
                      </Typography>
                      <Typography variant="body2">
                        Consumed Qty: {material.consumed_qty}
                      </Typography>
                      <Typography variant="body2">
                        Available Qty: {(material.allocated_qty ?? 0) - (material.consumed_qty ?? 0)}
                      </Typography>
                      <Typography variant="body2">
                        Is Sample: {material.is_sample ? 'Yes' : 'No'}
                      </Typography>
                    </Box>
                  </CardContent>
                  <CardActions>
                    {canEdit('designs') && (
                      <IconButton size="small" onClick={() => handleEdit(material.id!)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                    {canDelete('designs') && (
                      <IconButton size="small" onClick={() => handleDelete(material.id!)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box sx={{ height: 500, width: '100%' }}>
            <DataGrid
              rows={filteredMaterials}
              columns={makeSafeGridColumns(columns)}
              initialState={{
                pagination: {
                  paginationModel: { page: 0, pageSize: 10 },
                },
              }}
              pageSizeOptions={[10, 25, 50]}
              checkboxSelection={false}
              disableRowSelectionOnClick
            />
          </Box>
        )
      )}
      
      {/* Add/Edit Dialog */}
      {dialogOpen && (
        <DesignMaterialFormDialog
          open={dialogOpen}
          designMaterialId={selectedMaterialId}
          onClose={handleDialogClose}
          onSave={handleSaveSuccess}
        />
      )}
    </Box>
  );
};

// Original DesignMaterialPage component for routing
const DesignMaterialPage: React.FC = () => {
  return <DesignMaterialContent />;
};

export default DesignMaterialPage; 